import type { AdminUserSummary } from '@/versions/v1/shared/admin-seed';

export type SignupRow = {
  id: string;
  email: string;
  name: string | null;
  createdAt: number;
  disposition: 'pending' | 'rejected';
};

export type LobbyPanelSurface = 'tasks' | 'docs' | 'chat' | 'data' | 'generic';

export type LobbyPanel = {
  title: string;
  body: string;
  surface: LobbyPanelSurface;
};

export type LobbySettings = {
  enabled: boolean;
  killSwitch: boolean;
  contactEmail: string;
  allowedDomains: string;
  welcomeHeadline: string;
  welcomeSubline: string;
  panels: LobbyPanel[];
};

export const LOBBY_PANEL_SURFACES: readonly LobbyPanelSurface[] = [
  'tasks',
  'docs',
  'chat',
  'data',
  'generic',
] as const;

export const BULK_ADMIT_MAX = 50;

export const DEFAULT_LOBBY_SETTINGS: LobbySettings = {
  enabled: true,
  killSwitch: false,
  contactEmail: 'admin@example.com',
  allowedDomains: 'example.com, acme.co',
  welcomeHeadline: 'Welcome to the workspace',
  welcomeSubline: 'An admin will review your signup shortly.',
  panels: [
    {
      title: 'While you wait',
      body: 'Browse docs and prepare your first project.',
      surface: 'docs',
    },
  ],
};

export function parseDomainsInput(raw: string): string[] {
  return [
    ...new Set(
      raw
        .split(',')
        .map((part) => part.trim().toLowerCase())
        .filter((part) => part.length > 0),
    ),
  ];
}

export function toggleSignupSelection(
  selected: Set<string>,
  signupId: string,
  checked: boolean,
): Set<string> {
  const next = new Set(selected);
  if (checked) next.add(signupId);
  else next.delete(signupId);
  return next;
}

export function toggleSelectAllOnPage(
  selected: Set<string>,
  pageIds: string[],
  selectAll: boolean,
): Set<string> {
  const next = new Set(selected);
  if (selectAll) {
    for (const id of pageIds) next.add(id);
  } else {
    for (const id of pageIds) next.delete(id);
  }
  return next;
}

export function allOnPageSelected(
  selected: Set<string>,
  signups: SignupRow[],
): boolean {
  const selectable = signups.filter((signup) => signup.disposition === 'pending');
  return (
    selectable.length > 0 && selectable.every((signup) => selected.has(signup.id))
  );
}

export function filterUsersForAddMember(
  users: AdminUserSummary[],
  query: string,
  excludeEmails: Set<string>,
): AdminUserSummary[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  return users
    .filter((user) => !excludeEmails.has(user.email.toLowerCase()))
    .filter((user) => {
      const haystack = `${user.email} ${user.name ?? ''}`.toLowerCase();
      return haystack.includes(needle);
    })
    .slice(0, 8);
}
